// Перевірки ПН перед підписом. Одна функція для майстра, швидкого редактора й сервера.
// Повертає { blocking, warnings, unverified }: кожен пункт — { path, message }, path веде до поля
// (наприклад "buyer.ipn", "rows.2.uktzed", "date"). Тексти — людською мовою, без кодів XML.
import { RATES, calcRow, parseDec } from "./calc.js";
import { registrationDeadline, NONPAYER } from "./model.js";
import { encodeCp1251 } from "./cp1251.js";

const digits = (s) => String(s ?? "").replace(/\s/g, "");
// Формати кодів — дослівно з XSD (common_types.xsd: DGUKTZEDs, DGDKPP)
export const UKTZED = /^(\d{10}|\d{8}|\d{6}|00\d{3}|\d{4})$/;
export const DKPP = /^((\d{2}\.\d{2}\.\d{2}-\d{2}(\.\d{1,2})*)|(\d{2}\.\d{2}\.\d{2}(-\d{1,2})*)|(\d{2}\.\d{2}(\.\d{1,2})*))$/;
const dayDiff = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000);
const uaDate = (iso) => iso.split("-").reverse().join(".");
// Документ подається в кодуванні windows-1251: символів поза ним (×, emoji, деякі лапки) у ньому бути не може
const notEncodable = (s) => encodeCp1251(String(s ?? "")).bad;
const SWAP = { "×": "x", "≤": "<=", "≥": ">=", "⌀": "діам.", "Ø": "діам.", "²": "2", "³": "3" };
const charsMessage = (bad) => `символ${bad.length > 1 ? "и" : ""} ${bad.map((c) => `«${c}»`).join(", ")} не можна подати в ДПС (кодування windows-1251) — замініть${bad.some((c) => SWAP[c]) ? `, наприклад ${bad.filter((c) => SWAP[c]).map((c) => `«${c}» на «${SWAP[c]}»`).join(", ")}` : ""}`;

export function validatePn(doc, ctx = {}) {
  const blocking = [], warnings = [], unverified = [];
  const bad = (path, message) => blocking.push({ path, message });
  const warn = (path, message) => warnings.push({ path, message });
  const today = ctx.today || new Date().toISOString().slice(0, 10);

  // --- Продавець: підставляється автоматично; якщо чогось бракує — пояснюємо, звідки взяти
  const s = doc.seller || {};
  if (!s.name || !/^\d{12}$/.test(digits(s.ipn)) || !s.tin) {
    bad("seller", !s.name ? "Не вдалося підставити реквізити продавця: оновіть дані кабінету ДПС — беремо їх з даних компанії (картки платника)" : !/^\d{12}$/.test(digits(s.ipn)) ? "Немає ІПН продавця: у картці платника немає реєстрації платником ПДВ — оновіть дані кабінету ДПС" : "Не вдалося підставити код ЄДРПОУ продавця");
  }
  if (!s.reg || !s.raj || !s.sti) bad("seller", "Немає коду податкової інспекції продавця — синхронізуйте розділ ПН/РК");
  if (!s.director?.name || !/^\d{10}$/.test(digits(s.director?.rnokpp))) {
    bad("seller.director", "Введіть РНОКПП керівника (особи, що підписує ПН) — на кроці «Операція й покупець» або в «Дані компанії» → «Відомості про керівників»");
  }

  // --- Дата
  if (!/^\d{4}-\d{2}-\d{2}$/.test(doc.date || "")) bad("date", "Вкажіть дату накладної — дату першої події");
  else {
    if (doc.date > today) bad("date", `Дата накладної ${uaDate(doc.date)} ще не настала — ПН складається на дату події, що вже відбулась`);
    const dl = registrationDeadline(doc.date);
    if (dl && dl < today) warn("date", `Строк реєстрації минув ${uaDate(dl)}. Зареєструвати ще можна, але за порушення строку нараховується штраф`);
    else if (dl && dayDiff(dl, today) <= 3) warn("date", `Зареєструйте до ${uaDate(dl)} — лишилось днів: ${dayDiff(dl, today)}`);
  }

  // --- Номер
  // XSD DGI7nom: 1…9 999 999
  if (!/^[1-9]\d{0,6}$/.test(String(doc.number || "").trim())) bad("number", "Вкажіть порядковий номер накладної — ціле число від 1 до 9 999 999, наприклад 5");
  else if (ctx.issued?.some((i) => String(i.number) === String(doc.number).trim() && i.year === (doc.date || "").slice(0, 4))) {
    warn("number", `Номер ${doc.number} уже є серед ваших ПН за ${doc.date.slice(0, 4)} рік — перевірте, чи це не дубль`);
  }

  // --- Покупець
  const b = doc.buyer || {};
  if (doc.kind === "nonpayer") {
    if (b.ipn !== NONPAYER.ipn) bad("buyer.ipn", "Для покупця без ПДВ ІПН має бути умовний — 100000000000");
  } else {
    if (!String(b.name || "").trim()) bad("buyer.name", "Вкажіть назву покупця");
    else if (notEncodable(b.name).length) bad("buyer.name", `Назва покупця: ${charsMessage(notEncodable(b.name))}`);
    if (!/^\d{12}$/.test(digits(b.ipn))) bad("buyer.ipn", "Додайте ІПН покупця — 12 цифр, він є у свідоцтві або витязі з реєстру платників ПДВ");
    else if (/^[1-6]0{11}$/.test(digits(b.ipn))) bad("buyer.ipn", "Це умовний ІПН. Якщо покупець не платник ПДВ — оберіть «Продаж покупцю без ПДВ»");
    if (!/^(\d{8}|\d{10})$/.test(digits(b.tin))) bad("buyer.tin", "Додайте код покупця: ЄДРПОУ (8 цифр) або РНОКПП (10 цифр)");
    const chk = ctx.buyerCheck;
    if (chk?.vat === false) bad("buyer.ipn", "За реєстром ДПС покупець не є платником ПДВ. Оберіть «Продаж покупцю без ПДВ» або перевірте ІПН");
    else if (!chk || chk.vat == null) unverified.push({ path: "buyer.ipn", message: chk?.error ? `Реєстр платників ПДВ недоступний (${chk.error}) — статус покупця не перевірено` : "Статус платника ПДВ покупця не перевірено в реєстрі ДПС" });
  }

  // --- Рядки
  const rows = (doc.rows || []).filter((r) => String(r.name || "").trim() || r.price || r.sum);
  if (!rows.length) bad("rows", "Додайте хоча б один товар або послугу");
  rows.forEach((r) => {
    const i = doc.rows.indexOf(r), n = i + 1, p = (f) => `rows.${i}.${f}`;
    if (!String(r.name || "").trim()) bad(p("name"), `Рядок ${n}: вкажіть назву товару чи послуги`);
    else if (notEncodable(r.name).length) bad(p("name"), `Рядок ${n}: ${charsMessage(notEncodable(r.name))}`);
    if (notEncodable(r.unit).length) bad(p("unit"), `Рядок ${n}, одиниця: ${charsMessage(notEncodable(r.unit))}`);
    if (r.type === "service") {
      if (!DKPP.test(digits(r.dkpp))) bad(p("dkpp"), `Рядок ${n}: вкажіть код послуги за ДКПП, наприклад 43.21.10`);
    } else {
      const u = digits(r.uktzed);
      if (!UKTZED.test(u)) bad(p("uktzed"), `Рядок ${n}: вкажіть код товару за УКТ ЗЕД — 4, 6, 8 або 10 цифр (або умовний код ДПС 00XXX)`);
      else if (r.imported && u.length !== 10) bad(p("uktzed"), `Рядок ${n}: для ввезеного товару потрібен повний код УКТ ЗЕД — 10 цифр`);
      if (!String(r.unit || "").trim()) bad(p("unit"), `Рядок ${n}: вкажіть одиницю виміру`);
    }
    if (!(r.rate in RATES)) bad(p("rate"), `Рядок ${n}: оберіть ставку ПДВ`);
    if (r.rate === "903" && !/^\d{8}$/.test(digits(r.benefit))) bad(p("benefit"), `Рядок ${n}: для звільненої операції вкажіть код пільги — 8 цифр`);
    const q = parseDec(r.qty);
    if (q == null || q <= 0n) bad(p("qty"), `Рядок ${n}: кількість має бути більшою за нуль, до 12 знаків після коми`);
    if (r.amountBy === "sum") {
      const v = parseDec(r.sum, 2);
      if (v == null || v <= 0n) bad(p("sum"), `Рядок ${n}: вкажіть суму рядка, до 2 знаків після коми`);
    } else {
      const v = parseDec(r.price);
      if (v == null || v <= 0n) bad(p("price"), `Рядок ${n}: вкажіть ціну за одиницю, до 12 знаків після коми`);
    }
    if (r.rate in RATES && calcRow(r, doc.priceMode).ok && calcRow(r, doc.priceMode).base === 0n) {
      bad(p("price"), `Рядок ${n}: сума рядка після округлення дорівнює нулю`);
    }
  });
  // --- ПН за щоденними підсумками (тип причини 11, «Каси й продажі»): набір має відповідати чекам дня, з яких його складено —
  // дата, ставка й сума з ПДВ (допуск — копійка на рядок через округлення бази й ПДВ). Інакше — не подаємо (рев'ю хвилі 1, W1-02, W1-07)
  if (doc.kind === "nonpayer" && doc.reason === "11" && doc.daily) {
    const dl = doc.daily;
    if (doc.date !== dl.date) bad("date", `ПН за підсумками дня складається на день продажів — ${uaDate(dl.date)}`);
    const wrongRate = rows.filter((r) => String(r.rate) !== String(dl.rate));
    if (dl.rate && wrongRate.length) bad("rows", `Ставка ПДВ за Z-звітом дня — ${dl.rate}%; у рядках ${wrongRate.map((r) => `«${r.name}»`).slice(0, 3).join(", ")} — інша`);
    let total = 0n, ok = true;
    for (const r of rows) { const c = calcRow(r, doc.priceMode); if (c.ok) total += c.gross; else ok = false; }
    const expect = BigInt(Math.round(Number(dl.checksSum) * 100));
    if (ok && total !== expect) { // без допуску: рядки — точні суми позицій чеків
      bad("rows", `Сума рядків з ПДВ ${(Number(total) / 100).toFixed(2)} ≠ сума чеків дня ${Number(dl.checksSum).toFixed(2)} — набір має збігатися з чеками`);
    }
  }

  return { blocking, warnings, unverified, ok: blocking.length === 0 };
}
